import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PendientesPage.css';
import './SharedLoading.css';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API_BASE_URL from '../config';

const moment = require('moment-timezone');

const getUrgencia = (eta) => {
  if (!eta) return { nivel: 'sin-hora', label: 'Sin hora', minutos: null };
  const ahora = moment().tz('America/Mexico_City');
  const horaVuelo = moment(eta);
  const diff = horaVuelo.diff(ahora, 'minutes');
  if (diff < 0) return { nivel: 'pasado', label: 'Vuelo pasado', minutos: diff };
  if (diff <= 15) return { nivel: 'critico', label: `${diff} min`, minutos: diff };
  if (diff <= 30) return { nivel: 'alto', label: `${diff} min`, minutos: diff };
  if (diff <= 60) return { nivel: 'medio', label: `${diff} min`, minutos: diff };
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return { nivel: 'bajo', label: `${h}h ${m}m`, minutos: diff };
};

const PendingServicesPage = () => {
  const [pendingServices, setPendingServices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [search, setSearch] = useState('');
  const [loadingPage, setLoadingPage] = useState(true);
  const [asignando, setAsignando] = useState(false);
  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : {};
  const estacion = decoded.estacion?.trim().toUpperCase();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}api/servicios/pendientes-hoy`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const filtered = response.data.filter((s) =>
          s.statusServicio === 'PENDIENTE' &&
          s.estacion?.trim().toUpperCase() === estacion
        );

        setPendingServices(filtered);
      } catch (err) {
        console.error(err);
        toast.error('Error al cargar servicios pendientes.');
      } finally {
        setLoadingPage(false);
      }
    };

    if (token && estacion) fetchServices();
    const interval = setInterval(() => {
      if (token && estacion) fetchServices();
    }, 60000);
    return () => clearInterval(interval);
  }, [token, estacion]);

  const openAssignModal = async (servicio) => {
    setSelectedService(servicio);
    setIsModalOpen(true);
    try {
      const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const filtrados = res.data.filter(c => c.estacion?.trim().toUpperCase() === estacion);
      setColaboradores(filtrados);
    } catch (err) {
      toast.error('Error al obtener operadores.');
    }
  };

  const asignarServicio = async (operador) => {
    if (asignando) return;
    setAsignando(true);
    try {
      await axios.put(`${API_BASE_URL}api/servicios/${selectedService.noServicio}`, {
        noColaborador: operador.noColaborador,
        statusServicio: 'EN CURSO',
        sillaRuedas: operador.sillaAsignada,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servicio asignado correctamente');
      setIsModalOpen(false);
      setPendingServices(pendingServices.filter(s => s.noServicio !== selectedService.noServicio));
    } catch (err) {
      toast.error('Error al asignar servicio');
    } finally {
      setAsignando(false);
    }
  };

  const cancelarServicio = async (servicio) => {
    try {
      await axios.put(`${API_BASE_URL}api/servicios/${servicio.noServicio}`, {
        statusServicio: 'CANCELADO',
        horaFin: moment().tz('America/Mexico_City').format('YYYY-MM-DD HH:mm:ss')
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servicio cancelado');
      setPendingServices(pendingServices.filter(s => s.noServicio !== servicio.noServicio));
    } catch (err) {
      toast.error('Error al cancelar servicio');
    }
  };

  const filteredColaboradores = search.trim()
    ? colaboradores.filter(c =>
        c.siglas?.toLowerCase().includes(search.toLowerCase()) ||
        c.nombreCompleto?.toLowerCase().includes(search.toLowerCase())
      )
    : colaboradores;

  // Separar alertas críticas
  const serviciosConUrgencia = pendingServices.map(s => ({
    ...s,
    urgencia: getUrgencia(s.eta || s.horaVuelo),
  })).sort((a, b) => {
    if (a.urgencia.minutos === null) return 1;
    if (b.urgencia.minutos === null) return -1;
    return a.urgencia.minutos - b.urgencia.minutos;
  });

  const alertas = serviciosConUrgencia.filter(
    s => s.urgencia.nivel === 'critico' || s.urgencia.nivel === 'alto'
  );

  if (loadingPage) {
    return (
      <div className="pend-container">
        <div className="page-loading-overlay">
          <div className="page-loading-spinner" />
          <p className="page-loading-text">Cargando servicios pendientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pend-container">
      <ToastContainer />

      {/* Header */}
      <div className="pend-header">
        <h2 className="pend-title">Servicios Pendientes</h2>
        <span className="pend-count">{pendingServices.length} pendientes</span>
      </div>

      {/* Alertas de tiempo */}
      {alertas.length > 0 && (
        <div className="pend-alertas">
          <div className="pend-alertas-header">
            <i className="fa fa-exclamation-triangle" />
            <span>Alertas de tiempo — servicios que requieren atención inmediata</span>
          </div>
          <div className="pend-alertas-grid">
            {alertas.map(servicio => (
              <div key={`alerta-${servicio.noServicio}`} className={`pend-alerta-card pend-alerta-${servicio.urgencia.nivel}`}>
                <div className="pend-alerta-timer">
                  <span className="pend-alerta-mins">{servicio.urgencia.label}</span>
                  <span className="pend-alerta-sub">restantes</span>
                </div>
                <div className="pend-alerta-info">
                  <span className="pend-alerta-vuelo">{servicio.aerolinea} {servicio.noVuelo}</span>
                  <span className="pend-alerta-pax">{servicio.nombrePax || 'Sin nombre'}</span>
                </div>
                <button className="pend-alerta-btn" onClick={() => openAssignModal(servicio)}>
                  Asignar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid de servicios */}
      {serviciosConUrgencia.length === 0 ? (
        <div className="pend-empty">
          <i className="fa fa-check-circle" />
          <p>No hay servicios pendientes</p>
        </div>
      ) : (
        <div className="pend-grid">
          {serviciosConUrgencia.map(servicio => (
            <div key={servicio.noServicio} className={`pend-card pend-card-${servicio.urgencia.nivel}`}>
              <div className="pend-card-top">
                <span className="pend-card-no">#{servicio.noServicio}</span>
                <span className={`pend-card-badge pend-badge-${servicio.urgencia.nivel}`}>
                  {servicio.urgencia.label}
                </span>
              </div>
              <div className="pend-card-body">
                <div className="pend-card-row">
                  <span className="pend-card-label">Pasajero</span>
                  <span className="pend-card-value">{servicio.nombrePax || '—'}</span>
                </div>
                <div className="pend-card-row">
                  <span className="pend-card-label">Vuelo</span>
                  <span className="pend-card-value">{servicio.aerolinea} {servicio.noVuelo}</span>
                </div>
                <div className="pend-card-row">
                  <span className="pend-card-label">Silla</span>
                  <span className="pend-card-value">{servicio.sillaRuedas || '—'}</span>
                </div>
                <div className="pend-card-row">
                  <span className="pend-card-label">ETA</span>
                  <span className="pend-card-value">
                    {servicio.eta ? moment(servicio.eta).format('HH:mm') : servicio.horaVuelo ? moment(servicio.horaVuelo).format('HH:mm') : '—'}
                  </span>
                </div>
              </div>
              <div className="pend-card-actions">
                <button onClick={() => openAssignModal(servicio)} className="pend-btn-asignar">
                  <i className="fa fa-user-plus" /> Asignar
                </button>
                <button onClick={() => cancelarServicio(servicio)} className="pend-btn-cancelar">
                  <i className="fa fa-times" /> Cancelar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de asignación */}
      {isModalOpen && (
        <div className="pend-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="pend-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pend-modal-header">
              <div>
                <h3 className="pend-modal-title">Asignar operador</h3>
                <p className="pend-modal-sub">
                  Servicio #{selectedService?.noServicio} — {selectedService?.nombrePax}
                </p>
              </div>
              <button className="pend-modal-close" onClick={() => setIsModalOpen(false)}>
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="pend-modal-body">
              <input
                type="text"
                placeholder="Buscar por siglas o nombre..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pend-modal-search"
              />
              <div className="pend-colab-list">
                {filteredColaboradores.map(c => (
                  <div
                    key={c.noColaborador}
                    className="pend-colab-row"
                    onClick={() => asignarServicio(c)}
                  >
                    <div className="pend-colab-info">
                      <span className="pend-colab-siglas">{c.siglas}</span>
                      <span className="pend-colab-nombre">{c.nombreCompleto}</span>
                    </div>
                    <span className="pend-colab-silla">Silla: {c.noSilla || '—'}</span>
                  </div>
                ))}
                {filteredColaboradores.length === 0 && (
                  <p className="pend-colab-empty">No se encontraron operadores</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingServicesPage;
